import request, { IWorkspaceResponse } from '/@/api/http/request'
import type { LabelStudioImageSavePayload, LabelStudioResultItem } from '/@/utils/labelStudioAnnotation'
import { parseLabelStudioAnnotations } from '/@/utils/labelStudioAnnotation'
import { toAbsoluteMediaUrl } from '/@/utils/media-annotation'

const HTTP_PREFIX = '/media/api/v1/annotations'

/** 图片标注保存参数 */
export interface ImageAnnotationSaveParams {
  path: string
  annotation: string // JSON string of LabelStudioResultItem[]
}

/** 图片标注查询参数 */
export interface ImageAnnotationQueryParams {
  path: string
}

/** 图片标注查询响应 */
export interface ImageAnnotationQueryResponse {
  id: number
  path: string
  annotation: string // JSON string of LabelStudioResultItem[]
}

/** 保存图片标注（使用path和annotation字符串） */
export async function saveImageAnnotations (
  params: ImageAnnotationSaveParams
): Promise<IWorkspaceResponse<string>> {
  const url = '/pic/annotation/create'
  const result = await request.post(url, params)
  return result.data
}

/** 查询图片标注（使用path） */
export async function queryImageAnnotations (
  params: ImageAnnotationQueryParams
): Promise<IWorkspaceResponse<ImageAnnotationQueryResponse>> {
  const url = '/pic/annotation/query'
  const result = await request.post(url, params)
  return result.data
}

function parseRawBboxArray (
  values: number[],
  fallbackWidth: number,
  fallbackHeight: number
) {
  if (values.length < 4 || fallbackWidth <= 0 || fallbackHeight <= 0) return []
  const [x, y, width, height] = values
  const looksRelative =
    x >= 0 && y >= 0 && width >= 0 && height >= 0 &&
    x <= 100 && y <= 100 && width <= 100 && height <= 100
  const rect = looksRelative
    ? { x, y, width, height }
    : {
        x: (x / fallbackWidth) * 100,
        y: (y / fallbackHeight) * 100,
        width: (width / fallbackWidth) * 100,
        height: (height / fallbackHeight) * 100,
      }
  return [{
    id: 'preview-bbox-0',
    shape: 'rectangle' as const,
    label: '',
    origin: 'manual' as const,
    original_width: fallbackWidth,
    original_height: fallbackHeight,
    rect,
  }]
}

/** 解析查询返回的annotation JSON字符串为 AnnotationItem 列表 */
export function parseAnnotationString (
  annotationString: string,
  fallbackWidth = 0,
  fallbackHeight = 0
) {
  try {
    const parsed = JSON.parse(annotationString) as unknown
    if (
      Array.isArray(parsed) &&
      parsed.length >= 4 &&
      parsed.every((v) => typeof v === 'number')
    ) {
      return parseRawBboxArray(parsed as number[], fallbackWidth, fallbackHeight)
    }
    const result = parsed as LabelStudioResultItem[]
    return parseLabelStudioAnnotations({ result }, fallbackWidth, fallbackHeight)
  } catch (error) {
    console.error('Failed to parse annotation string:', error)
    return []
  }
}

/** 保存单张图片标注（Label Studio result 结构，无 project 字段） */
export async function saveImageAnnotationsV2 (
  payload: LabelStudioImageSavePayload
): Promise<IWorkspaceResponse<unknown>> {
  const url = `${HTTP_PREFIX}/save`
  const result = await request.post(url, payload)
  return result.data
}

export interface ModelTrainingListItem {
  imagePath: string
  annotation: LabelStudioResultItem[]
}

export interface BatchModelTrainingParams {
  job_id: string
  model_id: number
  video_path: string
  labels: string
  reference_images: ModelTrainingListItem[]
}

/** 提交模型训练（单张或多张） */
export async function batchModelTraining (
  params: BatchModelTrainingParams
): Promise<IWorkspaceResponse<unknown>> {
  const url = '/model/one/pic/train'
  const result = await request.post(url, {
    ...params,
    video_path: toAbsoluteMediaUrl(params.video_path),
    reference_images: params.reference_images.map((item) => ({
      ...item,
      imagePath: toAbsoluteMediaUrl(item.imagePath),
    })),
  })
  return result.data
}

/** 当前图片模型训练（复用批量接口，仅传一张） */
export function submitCurrentImageModelTraining (
  jobId: string,
  reference_images: ModelTrainingListItem[],
  modelId: number,
  videoPath: string,
  labels: string
): Promise<IWorkspaceResponse<unknown>> {
  return batchModelTraining({
    job_id: jobId,
    model_id: modelId,
    video_path: videoPath,
    labels,
    reference_images,
  })
}
